/** @jsx isolateComponent */
import { css } from '@emotion/core';
import type { MouseEvent as ReactMouseEvent } from 'react';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
/* eslint-disable import/no-duplicates */
import {
  Animation,
  Colors,
  Icon,
  TooltipWrapper,
  useTheme,
} from '@zapier/design-system';
import type { IconName } from '@zapier/design-system';

type Props = {
  /**
   * ARIA attribute indicating whether or not the content
   * controlled by the button is visible.
   */
  ariaExpanded: boolean;
  /**
   * ARIA attribute to label the button if `label` is empty.
   */
  ariaLabel?: string;
  /**
   * Name of the `Icon` to render.
   */
  icon: IconName;
  /**
   *  Indicates what the button does. Used for the `ariaLabel` on the
   * `Button` as well as the text for the `Tooltip`.
   * This prop should be supplied in most circumstances, but in cases
   * **when it's not desired be sure to pass `ariaLabel`!**
   */
  label: string;
  /**
   * Callback to be executed when the button is clicked.
   */
  onClick?: (e: ReactMouseEvent) => void;
};

const Styles = labelStyles('MenuCollapseButton', {
  root: (isRebrand: boolean) => css`
    width: 40px;
    height: 40px;
    border-radius: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${isRebrand ? Colors.neutral600 : Colors.gray60};
    transition: ${Animation.transitionValue};

    &:hover,
    &:focus {
      background-color: ${isRebrand ? 'none' : Colors.gray10};
      color: ${isRebrand ? Colors.neutral800 : Colors.gray60};
    }
  `,
});

/**
 * @private
 * `MenuCollapseButton` renders button to toggle whether the sidebar is open or closed.
 */
const MenuCollapseButton = (props: Props) => {
  const { isRebrand } = useTheme();

  return (
    <TooltipWrapper
      content={props.label}
      position="east"
      shouldCorrectPosition={false}
      showTooltipOnTouchDevices={false}
    >
      {() => (
        <button
          type="button"
          aria-expanded={props.ariaExpanded}
          aria-label={props.ariaLabel || props.label}
          css={Styles.root(isRebrand)}
          onClick={(event: ReactMouseEvent) => {
            if (props.onClick) {
              props.onClick(event);
            }
          }}
        >
          <Icon name={props.icon} size={30} />
        </button>
      )}
    </TooltipWrapper>
  );
};

MenuCollapseButton.defaultProps = {
  ariaExpanded: false,
  icon: 'navMenu',
  label: 'Menu',
};

export default MenuCollapseButton;
